import { ChevronRight, X } from 'lucide-react';
import {
  Children,
  createContext,
  useContext,
  useEffect,
  useRef,
  type CSSProperties,
  type ReactNode,
} from 'react';

import './PageEditor.css';

export const PageSectionOverlayContext = createContext(false);

// Placement alt and compact rich text commit on blur. Commit before a panel is removed.
export function blurPageEditorFocus() {
  const active = document.activeElement;
  if (active instanceof HTMLElement && active !== document.body) active.blur();
}

export function PageSectionsWorkspace({ children }: { children: ReactNode }) {
  return (
    <div
      className="home-editor page-sections"
      aria-label="Page sections"
      onFocusCapture={(event) => {
        const field = event.target;
        if (
          !(field instanceof HTMLElement) ||
          !field.matches('input, textarea, select, [contenteditable="true"]')
        )
          return;
        requestAnimationFrame(() => {
          if (window.innerWidth >= 1024 || !field.isConnected || document.activeElement !== field)
            return;
          if (
            field.closest('[data-page-content-editor]')?.getAttribute('data-keyboard-open') ===
            'true'
          )
            return;
          if (field.getBoundingClientRect().bottom > window.innerHeight - 182) {
            field.scrollIntoView({ block: 'center', behavior: 'instant' });
          }
        });
      }}
      style={{ '--section-count': Children.toArray(children).length } as CSSProperties}
    >
      <h2 className="page-sections__label">Sections · top to bottom</h2>
      {children}
      <p className="page-sections__empty">Choose a section to edit its content.</p>
    </div>
  );
}

export function PageSection({
  id,
  index,
  title,
  description,
  expanded,
  source = 'Editable here',
  onToggle,
  children,
}: {
  id: string;
  index: string;
  title: string;
  description: string;
  expanded: boolean;
  source?: string;
  onToggle(): void;
  children: ReactNode;
}) {
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!expanded || window.innerWidth >= 1024) return;
    const frame = requestAnimationFrame(() =>
      panel.current?.scrollIntoView?.({ block: 'start', behavior: 'instant' }),
    );
    return () => cancelAnimationFrame(frame);
  }, [expanded]);
  const overlayOpen = useContext(PageSectionOverlayContext);
  const close = () => {
    blurPageEditorFocus();
    onToggle();
    trigger.current?.focus();
  };
  useEffect(() => {
    if (!expanded || overlayOpen) return;
    const onEscape = (event: KeyboardEvent) => {
      if (
        event.key !== 'Escape' ||
        event.defaultPrevented ||
        (event.target instanceof Element &&
          event.target.closest(
            '[role="dialog"], [role="alertdialog"], [data-radix-popper-content-wrapper]',
          ))
      )
        return;
      event.preventDefault();
      close();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  });

  return (
    <section
      className="home-section page-section"
      data-expanded={expanded || undefined}
      style={{ '--section-row': Number(index) + 1 } as CSSProperties}
    >
      <button
        ref={trigger}
        className="home-section__trigger"
        type="button"
        aria-expanded={expanded}
        aria-controls={id}
        onClick={() => {
          if (expanded) close();
          else {
            blurPageEditorFocus();
            onToggle();
          }
        }}
      >
        <span className="home-section__index">{index}</span>
        <span className="home-section__heading">
          <strong>{title}</strong>
          <small>{source}</small>
        </span>
        <span className="home-section__meta">
          <ChevronRight size={18} aria-hidden="true" />
        </span>
      </button>
      {expanded ? (
        <section ref={panel} className="page-section__panel" id={id} aria-label={`${title} editor`}>
          <header className="page-section__header">
            <div>
              <h2>{title}</h2>
              <p>{description}</p>
            </div>
            <button type="button" onClick={close} aria-label={`Close ${title} editor`}>
              <X size={20} aria-hidden="true" />
            </button>
          </header>
          <div className="page-section__fields">{children}</div>
        </section>
      ) : null}
    </section>
  );
}
